// iter157.2 — Footer: sade, kurumsal, tek renk tonu (zinc/amber accent)
import { Link } from "react-router-dom";
import { MapPin, Phone, Mail, Globe, Facebook, Instagram, Twitter, Linkedin, Youtube, Navigation } from "lucide-react";
import { useCompany } from "../../context/CompanyContext";
import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import useShopTree from "../../hooks/useShopTree";

export default function PublicFooter() {
  const { company } = useCompany();
  const { tree } = useShopTree();
  const brandName = company?.company_name || "Bordo Seramik";
  const tagline = company?.tagline || "Yaşama değer katan detaylar";
  const brandPrimary = company?.brand_primary || "#8D1B3D";
  const logoDark = company?.logo_dark_url || company?.logo_url || "";
  const [stores, setStores] = useState([]);
  const socials = [
    ["facebook", Facebook], ["instagram", Instagram], ["twitter", Twitter],
    ["linkedin", Linkedin], ["youtube", Youtube],
  ].filter(([k]) => company?.[k]);
  const initial = (brandName.trim()[0] || "B").toUpperCase();

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/seo/stores");
        const list = Array.isArray(data) ? data : (data?.stores || data?.items || []);
        setStores(list);
      } catch { /* ignore */ }
    })();
  }, []);

  const cellPrimary = { background: brandPrimary };
  const dirsFor = (s) => s.maps?.google_directions
    || (s.lat && s.lng
        ? `https://www.google.com/maps/dir/?api=1&destination=${s.lat},${s.lng}`
        : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent((s.name || "") + " " + (s.address || ""))}`);

  const allCategories = (tree.categories || []);
  const allBrands = (tree.brands || []);

  return (
    <footer className="bg-zinc-950 text-zinc-400 mt-16" data-testid="public-footer">
      {/* Kategoriler & Markalar — sade kurumsal düzen */}
      <div className="border-b border-zinc-900">
        <div className="max-w-[1400px] mx-auto px-6 py-16">
          <div className="grid lg:grid-cols-12 gap-14">
            {/* Kategoriler ağacı — 8 kolon */}
            <div className="lg:col-span-8" data-testid="footer-categories-block">
              <div className="mb-8 pb-4 border-b border-zinc-900">
                <div className="text-[10px] uppercase tracking-[0.35em] text-zinc-600 font-semibold">Ürün Ailesi</div>
                <h3 className="text-white text-lg font-display font-medium mt-2 tracking-tight">Kategoriler</h3>
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-8" data-testid="footer-categories">
                {allCategories.length === 0
                  ? Array.from({length: 6}).map((_, i) => (
                      <div key={i}><div className="h-3 w-24 bg-white/[0.04] rounded animate-pulse"/></div>
                    ))
                  : allCategories.map(c => (
                    <div key={c.id} data-testid={`footer-cat-${c.slug || c.id}`}>
                      <Link to={`/kategori/${c.slug || c.id}`}
                            className="block text-white text-[13px] font-medium hover:text-amber-300 mb-3 tracking-tight transition-colors">
                        {c.name}
                      </Link>
                      {(c.children || []).length > 0 && (
                        <ul className="space-y-1.5">
                          {c.children.map(sc => (
                            <li key={sc.id}>
                              <Link to={`/kategori/${sc.slug || sc.id}`}
                                    className="text-[12px] text-zinc-500 hover:text-white block truncate leading-relaxed transition-colors">
                                {sc.name}
                              </Link>
                              {(sc.children || []).length > 0 && (
                                <ul className="mt-1 space-y-0.5 pl-3">
                                  {sc.children.slice(0, 6).map(gsc => (
                                    <li key={gsc.id}>
                                      <Link to={`/kategori/${gsc.slug || gsc.id}`}
                                            className="text-[11px] text-zinc-600 hover:text-zinc-300 block truncate transition-colors">
                                        {gsc.name}
                                      </Link>
                                    </li>
                                  ))}
                                </ul>
                              )}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  ))}
              </div>
            </div>

            {/* Markalar — sade grid, tek renk tonu */}
            <div className="lg:col-span-4" data-testid="footer-brands-block">
              <div className="mb-8 pb-4 border-b border-zinc-900">
                <div className="text-[10px] uppercase tracking-[0.35em] text-zinc-600 font-semibold">Güvenilir</div>
                <h3 className="text-white text-lg font-display font-medium mt-2 tracking-tight">Markalarımız</h3>
              </div>
              <div className="grid grid-cols-2 gap-x-5 gap-y-4" data-testid="footer-brands">
                {allBrands.length === 0
                  ? Array.from({length: 8}).map((_, i) => (
                      <div key={i} className="h-6 bg-white/[0.04] rounded animate-pulse"/>
                    ))
                  : allBrands.map(b => (
                    <Link key={b.id} to={`/urunler?marka=${b.id}`} title={b.name}
                          data-testid={`footer-brand-${b.slug || b.id}`}
                          className="group flex items-center gap-2.5 py-1">
                      {b.logo && (
                        <div className="w-8 h-8 rounded bg-white flex items-center justify-center shrink-0 overflow-hidden opacity-85 group-hover:opacity-100 transition-opacity">
                          <img src={b.logo} alt={b.name} className="max-h-full max-w-full object-contain p-0.5"/>
                        </div>
                      )}
                      <div className="min-w-0 flex-1">
                        <div className="text-[12px] font-medium text-zinc-300 truncate group-hover:text-white transition-colors leading-tight">{b.name}</div>
                        {b.product_count > 0 && (
                          <div className="text-[9px] text-zinc-600 mt-0.5">{b.product_count} ürün</div>
                        )}
                      </div>
                    </Link>
                  ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-6 py-12 grid grid-cols-2 md:grid-cols-6 gap-8 text-sm">
        {/* Marka */}
        <div className="col-span-2 md:col-span-2">
          <div className="flex items-center gap-2 mb-3">
            {logoDark ? (
              <img src={logoDark} alt={brandName} className="h-10 max-w-[140px] object-contain"/>
            ) : (
              <>
                <div className="w-9 h-9 rounded text-white flex items-center justify-center font-bold font-display" style={cellPrimary}>{initial}</div>
                <span className="font-display font-bold text-white text-xl">{brandName}</span>
              </>
            )}
          </div>
          <p className="text-xs leading-relaxed">{tagline}</p>
          <p className="text-xs leading-relaxed mt-3 opacity-70 max-w-xs">
            2014'ten bu yana Adana ve Çukurova bölgesinde kaliteli seramik, granit ve yapı malzemeleri sunuyoruz.
          </p>
          {socials.length > 0 && (
            <div className="flex items-center gap-2 mt-5">
              {socials.map(([key, Icon]) => (
                <a key={key} href={company[key]} target="_blank" rel="noreferrer"
                   className="w-9 h-9 rounded-full border border-zinc-700 flex items-center justify-center hover:text-white hover:border-white transition-colors"
                   data-testid={`footer-social-${key}`}>
                  <Icon size={14}/>
                </a>
              ))}
            </div>
          )}
        </div>

        {/* Alışveriş */}
        <div>
          <h4 className="text-white font-semibold mb-3 text-sm tracking-wide">Alışveriş</h4>
          <ul className="space-y-2 text-xs">
            <li><Link to="/firsatlar" className="hover:text-white">Fırsatlar</Link></li>
            <li><Link to="/hesap/giris" className="hover:text-white">Giriş Yap</Link></li>
            <li><Link to="/hesap/giris?tab=register" className="hover:text-white">Kayıt Ol</Link></li>
            <li><Link to="/sepet" className="hover:text-white">Sepetim</Link></li>
            <li><Link to="/blog" className="hover:text-white">Blog & Rehber</Link></li>
            <li><Link to="/sss" className="hover:text-white">SSS</Link></li>
            <li><Link to="/kvkk" className="hover:text-white">KVKK</Link></li>
            <li><Link to="/gizlilik" className="hover:text-white">Gizlilik</Link></li>
            <li><Link to="/iade-degisim" className="hover:text-white">İade & Değişim</Link></li>
            <li><Link to="/mesafeli-satis" className="hover:text-white">Mesafeli Satış</Link></li>
          </ul>
        </div>

        {/* Kurumsal */}
        <div>
          <h4 className="text-white font-semibold mb-3 text-sm tracking-wide">Kurumsal</h4>
          <ul className="space-y-2 text-xs">
            <li><Link to="/hakkimizda" className="hover:text-white">Hakkımızda</Link></li>
            <li><Link to="/iletisim" className="hover:text-white">İletişim</Link></li>
            <li><Link to="/magazalar" className="hover:text-white">Mağazalarımız</Link></li>
            <li><Link to="/yardim" className="hover:text-white">Yardım & Destek</Link></li>
          </ul>
        </div>

        {/* İletişim */}
        <div>
          <h4 className="text-white font-semibold mb-3 text-sm tracking-wide">İletişim</h4>
          <ul className="space-y-2 text-xs">
            {company?.phone && (
              <li className="flex items-start gap-2"><Phone size={12} className="mt-0.5 shrink-0"/>
                <a href={`tel:${company.phone.replace(/[^0-9+]/g, "")}`} className="hover:text-white">{company.phone}</a>
              </li>
            )}
            {company?.email && (
              <li className="flex items-start gap-2"><Mail size={12} className="mt-0.5 shrink-0"/>
                <a href={`mailto:${company.email}`} className="hover:text-white break-all">{company.email}</a>
              </li>
            )}
            {company?.website && (
              <li className="flex items-start gap-2"><Globe size={12} className="mt-0.5 shrink-0"/>
                <a href={company.website} target="_blank" rel="noreferrer" className="hover:text-white break-all">{company.website.replace(/^https?:\/\//, "")}</a>
              </li>
            )}
            <li><Link to="/yardim" className="hover:text-white flex items-center gap-1">→ Yardım & Destek Merkezi</Link></li>
          </ul>
        </div>

        {/* Mağazalar */}
        <div>
          <h4 className="text-white font-semibold mb-3 text-sm tracking-wide">Mağazalar</h4>
          {stores.length === 0 ? (
            <div className="text-xs opacity-70">Yakında…</div>
          ) : (
            <ul className="space-y-4 text-xs">
              {stores.slice(0, 4).map((s) => (
                <li key={s.id} className="border-l-2 border-zinc-800 pl-2.5" data-testid={`footer-store-${s.id}`}>
                  <div className="text-white text-[11px] font-semibold">{s.name || s.district || "Mağaza"}</div>
                  <div className="text-[11px] leading-snug opacity-80 mt-0.5">{s.address}</div>
                  <a href={dirsFor(s)} target="_blank" rel="noopener noreferrer"
                     className="inline-flex items-center gap-1 mt-1.5 text-[10px] font-semibold text-amber-300 hover:text-amber-200"
                     data-testid={`footer-store-dir-${s.id}`}>
                    <Navigation size={10}/> Yol Tarifi
                  </a>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <div className="border-t border-zinc-900 py-4 text-center text-[11px] px-4">
        <div>© {new Date().getFullYear()} {brandName} • Tüm hakları saklıdır</div>
        {company?.footer_note && <div className="opacity-60 mt-1">{company.footer_note}</div>}
      </div>
    </footer>
  );
}
